import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaCar, FaPlus, FaTrash, FaTimes, FaQrcode } from "react-icons/fa";

const D2Cars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedCarForQr, setSelectedCarForQr] = useState(null);
  const [imageMode, setImageMode] = useState("url"); // "url" or "upload"
  const [videoMode, setVideoMode] = useState("url"); // "url" or "upload"
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const handleMediaUpload = async (e, type = "image") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "video" && file.size > 100 * 1024 * 1024) {
      toast.error("Video file exceeds 100MB limit.");
      return;
    }
    if (type === "image" && file.size > 10 * 1024 * 1024) {
      toast.error("Image file exceeds 10MB limit.");
      return;
    }

    setUploadingMedia(true);
    const toastId = toast.loading(`Uploading ${type} to S3 bucket...`);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("media", file);

      const res = await fetch(`${getBackendBaseUrl()}/d2-cars/upload-media`, {
        method: "POST",
        body: uploadFormData
      });
      const data = await res.json();
      if (data.success && data.data?.url) {
        toast.update(toastId, {
          render: `✓ ${type === 'video' ? 'Video' : 'Image'} uploaded to S3 successfully!`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
        if (type === "video") {
          setFormData(prev => ({ ...prev, videoUrl: data.data.url }));
        } else {
          setFormData(prev => ({ ...prev, imageUrl: data.data.url }));
        }
      } else {
        toast.update(toastId, {
          render: data.message || `Failed to upload ${type}`,
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      }
    } catch (err) {
      console.error(`Error uploading ${type} to S3:`, err);
      toast.update(toastId, {
        render: `Failed to upload ${type} to S3`,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploadingMedia(false);
    }
  };
  const [formData, setFormData] = useState({
    stockNumber: "",
    vin: "",
    year: new Date().getFullYear(),
    make: "",
    model: "",
    trim: "",
    trims: [],
    colors: [],
    mileage: 0,
    msrp: 0,
    salePrice: 0,
    imageUrl: "",
    videoUrl: "",
    docFee: 800,
    registration: 772
  });

  const handleOpenAddModal = () => {
    setEditingCarId(null);
    setFormData({
      stockNumber: `TY${Math.floor(10000 + Math.random() * 90000)}`,
      vin: `4T1B11HK${Math.floor(100000 + Math.random() * 900000)}`,
      year: new Date().getFullYear(),
      make: "Toyota",
      model: "Camry",
      trim: "Base Model (LE)",
      trims: [
        {
          name: "Base Model (LE)",
          isBase: true,
          price: 28400,
          msrp: 28400,
          description: "Standard Hybrid Efficiency & Safety Features",
          colors: [
            { name: "Midnight Black", hex: "#0f172a", imageUrl: "", imageMode: "url" },
            { name: "Ice Cap White", hex: "#f8fafc", imageUrl: "", imageMode: "url" },
            { name: "Reservoir Blue", hex: "#1e3a8a", imageUrl: "", imageMode: "url" }
          ]
        },
        {
          name: "Top Model (XSE)",
          isBase: false,
          price: 34600,
          msrp: 34600,
          description: "Sport-Tuned Luxury, Dual Exhaust & 19\" Sport Wheels",
          colors: [
            { name: "Supersonic Red", hex: "#dc2626", imageUrl: "", imageMode: "url" },
            { name: "Wind Chill Pearl", hex: "#ffffff", imageUrl: "", imageMode: "url" },
            { name: "Heavy Metal Gray", hex: "#475569", imageUrl: "", imageMode: "url" }
          ]
        }
      ],
      colors: [{ name: "Midnight Black", hex: "#0f172a", imageUrl: "" }],
      mileage: 8,
      msrp: 28400,
      salePrice: 28400,
      imageUrl: "",
      videoUrl: "",
      docFee: 800,
      registration: 772
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (car) => {
    setEditingCarId(car._id);
    const carTrims = (car.trims && car.trims.length > 0)
      ? car.trims.map(t => ({
          ...t,
          colors: t.colors?.map(c => ({ ...c, imageMode: "url" })) || []
        }))
      : [
          {
            name: car.trim || "Base Model (LE)",
            isBase: true,
            price: car.salePrice || 28400,
            msrp: car.msrp || 28400,
            description: "Standard Model",
            colors: car.colors?.length > 0 ? car.colors.map(c => ({ ...c, imageMode: "url" })) : [{ name: car.color || "Midnight Black", hex: "#0f172a", imageUrl: car.imageUrl || "", imageMode: "url" }]
          },
          {
            name: "Top Model (XSE)",
            isBase: false,
            price: (car.salePrice || 28400) + 6200,
            msrp: (car.msrp || 28400) + 6200,
            description: "Sport & Luxury Top Model",
            colors: [
              { name: "Supersonic Red", hex: "#dc2626", imageUrl: "", imageMode: "url" },
              { name: "Wind Chill Pearl", hex: "#ffffff", imageUrl: "", imageMode: "url" }
            ]
          }
        ];

    setFormData({
      stockNumber: car.stockNumber || "",
      vin: car.vin || "",
      year: car.year || new Date().getFullYear(),
      make: car.make || "",
      model: car.model || "",
      trim: car.trim || carTrims[0]?.name || "",
      trims: carTrims,
      colors: car.colors?.length > 0 ? car.colors : (car.color ? [{ name: car.color, hex: "#000000", imageUrl: "" }] : []),
      mileage: car.mileage || 0,
      msrp: car.msrp || 0,
      salePrice: car.salePrice || 0,
      imageUrl: car.imageUrl || "",
      videoUrl: car.videoUrl || "",
      docFee: car.docFee !== undefined ? car.docFee : 800,
      registration: car.registration !== undefined ? car.registration : 772
    });
    setShowModal(true);
  };

  const handleAddTrim = () => {
    setFormData(prev => ({
      ...prev,
      trims: [
        ...(prev.trims || []),
        {
          name: `Model Tier ${(prev.trims?.length || 0) + 1}`,
          isBase: false,
          price: (prev.salePrice || 28400) + 4000,
          msrp: (prev.msrp || 28400) + 4000,
          description: "Premium Upgrades & Packages",
          colors: [
            { name: "Metallic Blue", hex: "#1e3a8a", imageUrl: "", imageMode: "url" },
            { name: "Pearl White", hex: "#f8fafc", imageUrl: "", imageMode: "url" }
          ]
        }
      ]
    }));
  };

  const handleRemoveTrim = (tIdx) => {
    setFormData(prev => ({
      ...prev,
      trims: prev.trims.filter((_, i) => i !== tIdx)
    }));
  };

  const handleTrimChange = (tIdx, field, value) => {
    const updated = [...(formData.trims || [])];
    updated[tIdx][field] = value;
    if (field === "isBase" && value === true) {
      updated.forEach((t, i) => { if (i !== tIdx) t.isBase = false; });
    }
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleAddColorToTrim = (tIdx) => {
    const updated = [...(formData.trims || [])];
    updated[tIdx].colors = [
      ...(updated[tIdx].colors || []),
      { name: "", hex: "#1e3a8a", imageUrl: "", imageMode: "url" }
    ];
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleRemoveColorFromTrim = (tIdx, cIdx) => {
    const updated = [...(formData.trims || [])];
    updated[tIdx].colors = updated[tIdx].colors.filter((_, i) => i !== cIdx);
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleColorChangeInTrim = (tIdx, cIdx, field, value) => {
    const updated = [...(formData.trims || [])];
    updated[tIdx].colors[cIdx][field] = value;
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleTrimColorFileChange = (e, tIdx, cIdx) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updated = [...(formData.trims || [])];
        updated[tIdx].colors[cIdx].imageUrl = reader.result;
        setFormData(prev => ({ ...prev, trims: updated }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddFeatureToTrim = (tIdx, featureText) => {
    if (!featureText) return;
    const updated = [...(formData.trims || [])];
    const currentFeatures = updated[tIdx].features || [];
    updated[tIdx].features = [...currentFeatures, featureText];
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleRemoveFeatureFromTrim = (tIdx, fIdx) => {
    const updated = [...(formData.trims || [])];
    if (updated[tIdx].features) {
      updated[tIdx].features = updated[tIdx].features.filter((_, i) => i !== fIdx);
    }
    setFormData(prev => ({ ...prev, trims: updated }));
  };

  const handleFileChange = (e, index = null) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image file size exceeds 5MB limit.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (index !== null) {
          const newColors = [...formData.colors];
          newColors[index].imageUrl = reader.result;
          setFormData(prev => ({ ...prev, colors: newColors }));
        } else {
          setFormData(prev => ({ ...prev, imageUrl: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchCars = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-cars`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setCars(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch cars", err);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCar = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");

      // Set base trim defaults
      const baseTrim = formData.trims?.find(t => t.isBase) || formData.trims?.[0];
      const basePrice = baseTrim ? Number(baseTrim.price) : Number(formData.salePrice || 28400);
      const baseMsrp = baseTrim ? Number(baseTrim.msrp) : Number(formData.msrp || 28400);
      // Deduplicate colors across trims by color name
      const rawColors = formData.trims?.flatMap(t => t.colors || []) || formData.colors || [];
      const uniqueColorsMap = new Map();
      rawColors.forEach(c => {
        if (c && c.name && !uniqueColorsMap.has(c.name.toLowerCase().trim())) {
          uniqueColorsMap.set(c.name.toLowerCase().trim(), { name: c.name, hex: c.hex || "#111111", imageUrl: c.imageUrl || "" });
        }
      });
      const allTrimColors = Array.from(uniqueColorsMap.values());

      const payload = {
        ...formData,
        salePrice: basePrice,
        msrp: baseMsrp,
        trim: baseTrim ? baseTrim.name : formData.trim,
        trims: formData.trims,
        variants: formData.trims,
        colors: allTrimColors,
        color: allTrimColors[0]?.name || "Standard Finish"
      };

      const url = editingCarId 
        ? `${getBackendBaseUrl()}/d2-cars/${editingCarId}`
        : `${getBackendBaseUrl()}/d2-cars`;
      
      const method = editingCarId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        toast.success(editingCarId ? "Vehicle & Model Trims updated!" : "Vehicle created successfully!");
        setShowModal(false);
        setEditingCarId(null);
        fetchCars();
      } else {
        toast.error(data.message || "Failed to save car");
      }
    } catch (err) {
      console.error("Save car error", err);
      toast.error("Failed to connect to server");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this car?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-cars/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Car deleted successfully");
        fetchCars();
      }
    } catch (err) {
      toast.error("Failed to delete car");
    }
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl shadow-sm border border-blue-100 animate-pulse">
              <FaCar size={24} />
            </span>
            Car Master Data
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Manage your inventory database, configure vehicle attributes, media video links, and set pricing.
          </p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
        >
          <FaPlus size={12} /> Add New Car
        </button>
      </div>

      {/* Inventory Cards Grid */}
      {loading ? (
        <div className="flex h-64 items-center justify-center text-slate-450 text-sm">
          <div className="flex flex-col items-center justify-center gap-2">
            <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
            Loading showroom inventory...
          </div>
        </div>
      ) : cars.length === 0 ? (
        <div className="bg-white border border-slate-200/60 rounded-3xl p-12 text-center text-slate-400 text-sm italic shadow-sm">
          No cars found in database. Click "+ Add New Car" to add your first vehicle.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cars.map((car) => {
            const displayImage = car.imageUrl || "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&q=80&w=400";
            return (
              <div key={car._id} className="bg-white rounded-3xl border border-slate-200/60 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                <div>
                  {/* Car Image Header */}
                  <div className="h-44 bg-slate-100 overflow-hidden relative border-b border-slate-100">
                    <img 
                      src={displayImage} 
                      alt={`${car.year} ${car.make} ${car.model}`}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Stock: {car.stockNumber}
                    </span>
                    {car.videoUrl && (
                      <span className="absolute top-4 right-4 bg-cyan-600/90 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                        🎬 Video Live
                      </span>
                    )}
                  </div>

                  {/* Body Content */}
                  <div className="p-5 space-y-4">
                    <div>
                      <h3 className="text-base font-extrabold text-slate-800 leading-tight">
                        {car.year} {car.make} {car.model}
                      </h3>
                      <p className="text-slate-400 text-xs mt-0.5 font-medium">{car.trim || "Standard Trim"}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs border-t border-slate-100 pt-3 text-slate-650">
                      <div>
                        <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Mileage</span>
                        <span className="font-semibold">{car.mileage?.toLocaleString() || 0} mi</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Exterior Color</span>
                        <span className="font-semibold truncate block">{car.color || "N/A"}</span>
                      </div>
                      <div className="col-span-2 border-t border-slate-50 pt-2 flex justify-between items-baseline">
                        <div>
                          <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">MSRP</span>
                          <span className="text-slate-400 line-through font-semibold">${car.msrp?.toLocaleString()}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400 block text-[9px] font-bold uppercase tracking-wider">Sale Price</span>
                          <span className="text-emerald-600 font-extrabold text-base">${car.salePrice?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Controls with Edit Button */}
                <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEditModal(car)}
                    className="bg-indigo-50 hover:bg-indigo-100 text-indigo-600 font-bold text-xs tracking-wide px-3.5 py-2.5 rounded-xl border border-indigo-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCarForQr(car);
                      setShowQrModal(true);
                    }}
                    className="flex-1 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs tracking-wide py-2.5 rounded-xl border border-slate-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <FaQrcode size={12} className="text-slate-500" /> Kiosk QR
                  </button>
                  <button
                    onClick={() => handleDelete(car._id)}
                    className="bg-red-50 hover:bg-red-100 text-red-600 font-bold text-xs tracking-wide px-3.5 py-2.5 rounded-xl border border-red-100 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add New Car Dialog Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative text-slate-800">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-655 p-1.5 hover:bg-slate-100 rounded-full transition-colors border-0 outline-none"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-850 tracking-tight mb-2">
              {editingCarId ? "Edit Vehicle & Media" : "Add New Vehicle"}
            </h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              {editingCarId 
                ? "Update vehicle specifications, pricing, image files, and video showcase links."
                : "Enter vehicle specifications and pricing to save it to dealership inventory."}
            </p>
            
            <form onSubmit={handleSaveCar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Stock Number *</label>
                <input required type="text" name="stockNumber" value={formData.stockNumber} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. S10293" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">VIN *</label>
                <input required type="text" name="vin" value={formData.vin} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="17-character VIN" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Year *</label>
                <input required type="number" name="year" value={formData.year} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Make *</label>
                <input required type="text" name="make" value={formData.make} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. Toyota" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Model *</label>
                <input required type="text" name="model" value={formData.model} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. Camry" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Trim</label>
                <input type="text" name="trim" value={formData.trim} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" placeholder="e.g. XSE V6" />
              </div>
 
              
              {/* VEHICLE TRIM MODELS & MODEL-SPECIFIC COLORS HIERARCHY */}
              <div className="col-span-1 sm:col-span-2 bg-slate-50/80 border border-slate-200 p-4 rounded-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 uppercase">
                      Vehicle Models &amp; Trims (Base vs Top Model)
                    </label>
                    <span className="text-[10px] text-slate-500">
                      Configure base and premium trim tiers with individual color palettes.
                    </span>
                  </div>
                  <button 
                    type="button" 
                    onClick={handleAddTrim} 
                    className="text-[10px] font-black px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <FaPlus size={9} /> Add Trim Model
                  </button>
                </div>

                {formData.trims && formData.trims.map((trimItem, tIdx) => (
                  <div key={tIdx} className={`p-4 rounded-2xl border transition-all space-y-3 relative ${
                    trimItem.isBase 
                      ? 'bg-cyan-50/50 border-cyan-300 shadow-sm' 
                      : 'bg-white border-slate-200 shadow-sm'
                  }`}>
                    <button 
                      type="button" 
                      onClick={() => handleRemoveTrim(tIdx)} 
                      className="absolute top-3 right-3 text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                    >
                      <FaTimes size={13} />
                    </button>

                    {/* Trim Model Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                      <div className="sm:col-span-4">
                        <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">
                          Trim Model Name *
                        </label>
                        <input 
                          type="text" 
                          value={trimItem.name} 
                          onChange={(e) => handleTrimChange(tIdx, "name", e.target.value)} 
                          className="w-full border border-slate-300 rounded-lg p-2 text-xs font-bold bg-white outline-none focus:border-indigo-500" 
                          placeholder="e.g. Base Model (LE)" 
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">
                          Price ($) *
                        </label>
                        <input 
                          type="number" 
                          value={trimItem.price} 
                          onChange={(e) => handleTrimChange(tIdx, "price", e.target.value)} 
                          className="w-full border border-slate-300 rounded-lg p-2 text-xs font-mono font-bold bg-white outline-none focus:border-indigo-500" 
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">
                          MSRP ($) *
                        </label>
                        <input 
                          type="number" 
                          value={trimItem.msrp} 
                          onChange={(e) => handleTrimChange(tIdx, "msrp", e.target.value)} 
                          className="w-full border border-slate-300 rounded-lg p-2 text-xs font-mono font-bold bg-white outline-none focus:border-indigo-500" 
                        />
                      </div>

                      <div className="sm:col-span-2 flex items-center gap-1.5 pt-3 sm:pt-0">
                        <input 
                          type="checkbox" 
                          id={`base-model-${tIdx}`}
                          checked={!!trimItem.isBase} 
                          onChange={(e) => handleTrimChange(tIdx, "isBase", e.target.checked)} 
                          className="w-4 h-4 text-cyan-600 rounded cursor-pointer" 
                        />
                        <label htmlFor={`base-model-${tIdx}`} className="text-[11px] font-bold text-slate-700 cursor-pointer">
                          Base Model
                        </label>
                      </div>
                    </div>

                    {/* Trim Model Features List */}
                    <div className="border-t border-slate-100 pt-2 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          Features &amp; Specs for {trimItem.name || `Model ${tIdx + 1}`} ({trimItem.features?.length || 0})
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 items-center">
                        {trimItem.features && trimItem.features.map((feat, fIdx) => (
                          <span key={fIdx} className="inline-flex items-center gap-1 text-[10px] font-bold bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-200 shadow-2xs">
                            ✨ {feat}
                            <button 
                              type="button" 
                              onClick={() => handleRemoveFeatureFromTrim(tIdx, fIdx)} 
                              className="text-indigo-400 hover:text-red-500 cursor-pointer ml-1 font-black"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                        {(!trimItem.features || trimItem.features.length === 0) && (
                          <span className="text-[10px] italic text-slate-400">No trim features added yet.</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <input 
                          type="text" 
                          id={`trim-feature-input-${tIdx}`}
                          placeholder="e.g. 19-inch Alloy Wheels, Sunroof, JBL Audio" 
                          className="flex-1 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-indigo-500 bg-white" 
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (e.target.value.trim()) {
                                handleAddFeatureToTrim(tIdx, e.target.value.trim());
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button 
                          type="button" 
                          onClick={() => {
                            const inp = document.getElementById(`trim-feature-input-${tIdx}`);
                            if (inp && inp.value.trim()) {
                              handleAddFeatureToTrim(tIdx, inp.value.trim());
                              inp.value = '';
                            }
                          }} 
                          className="text-[10px] font-bold px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg cursor-pointer shadow-2xs transition-colors"
                        >
                          + Add Feature
                        </button>
                      </div>
                    </div>

                    {/* Model-specific Colors List */}
                    <div className="border-t border-slate-100 pt-2 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          Colors Available for {trimItem.name || `Model ${tIdx + 1}`} ({trimItem.colors?.length || 0})
                        </span>
                        <button 
                          type="button" 
                          onClick={() => handleAddColorToTrim(tIdx)} 
                          className="text-[9px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 flex items-center gap-1 cursor-pointer"
                        >
                          + Add Color to {trimItem.name?.split(' ')[0] || 'Model'}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {trimItem.colors && trimItem.colors.map((c, cIdx) => (
                          <div key={cIdx} className="p-2.5 rounded-xl border border-slate-200 bg-slate-50 flex flex-col gap-2 relative">
                            <button 
                              type="button" 
                              onClick={() => handleRemoveColorFromTrim(tIdx, cIdx)} 
                              className="absolute top-1.5 right-1.5 text-slate-400 hover:text-red-500 p-0.5"
                            >
                              <FaTimes size={10} />
                            </button>

                            <div className="flex items-center gap-2">
                              <input 
                                type="color" 
                                value={c.hex || '#000000'} 
                                onChange={(e) => handleColorChangeInTrim(tIdx, cIdx, "hex", e.target.value)} 
                                className="w-6 h-6 p-0 border-0 rounded cursor-pointer flex-shrink-0" 
                              />
                              <input 
                                type="text" 
                                value={c.name} 
                                onChange={(e) => handleColorChangeInTrim(tIdx, cIdx, "name", e.target.value)} 
                                placeholder="Color name (e.g. Supersonic Red)" 
                                className="flex-1 border border-slate-200 rounded p-1 text-xs bg-white outline-none focus:border-indigo-400" 
                              />
                            </div>

                            <div className="flex items-center gap-1.5">
                              <input 
                                type="text" 
                                value={c.hex || ''} 
                                onChange={(e) => handleColorChangeInTrim(tIdx, cIdx, "hex", e.target.value)} 
                                placeholder="#hex" 
                                className="w-20 border border-slate-200 rounded p-1 text-[10px] uppercase font-mono bg-white" 
                              />
                              <input 
                                type="text" 
                                value={c.imageUrl || ''} 
                                onChange={(e) => handleColorChangeInTrim(tIdx, cIdx, "imageUrl", e.target.value)} 
                                placeholder="Image URL (optional)" 
                                className="flex-1 border border-slate-200 rounded p-1 text-[10px] bg-white" 
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Mileage *</label>
                <input required type="number" name="mileage" value={formData.mileage} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
 
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">MSRP ($) *</label>
                <input required type="number" name="msrp" value={formData.msrp} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Sale Price ($) *</label>
                <input required type="number" name="salePrice" value={formData.salePrice} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" />
              </div>
 
              
              {/* VEHICLE PREVIEW VIDEO SOURCE (URL OR S3 UPLOAD) */}
              <div className="col-span-1 sm:col-span-2">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold text-slate-600 uppercase">Vehicle Preview Video (Optional)</label>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => setVideoMode("url")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        videoMode === "url" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Use URL
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setVideoMode("upload")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        videoMode === "upload" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Upload Video (S3)
                    </button>
                  </div>
                </div>

                {videoMode === "url" ? (
                  <input 
                    type="text" 
                    name="videoUrl" 
                    value={formData.videoUrl} 
                    onChange={handleInputChange} 
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" 
                    placeholder="e.g. https://... or MP4/YouTube link" 
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex-1 border-2 border-dashed border-slate-200 hover:border-indigo-500 hover:bg-slate-50/50 rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all">
                      <span className="text-xs text-indigo-600 font-bold">Choose Video File &rarr; Upload to S3</span>
                      <span className="text-[10px] text-slate-400 mt-1">MP4, MOV, WEBM, MKV (Max 100MB)</span>
                      <input 
                        type="file" 
                        accept="video/*" 
                        onChange={(e) => handleMediaUpload(e, "video")} 
                        className="hidden" 
                      />
                    </label>
                    {formData.videoUrl && (
                      <div className="w-24 h-16 rounded-xl border border-slate-200 overflow-hidden relative flex-shrink-0 shadow-sm bg-slate-950">
                        <video src={formData.videoUrl} className="w-full h-full object-cover" muted loop autoPlay />
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, videoUrl: "" }))}
                          className="absolute inset-0 bg-red-600/90 text-white font-bold text-[10px] uppercase flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity border-0 cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {formData.videoUrl && videoMode === "upload" && (
                  <p className="text-[10px] text-emerald-600 font-bold mt-1 truncate">
                    ✓ Video uploaded to S3: {formData.videoUrl}
                  </p>
                )}
              </div>

              {/* VEHICLE IMAGE SOURCE (URL OR S3 UPLOAD) */}
              <div className="col-span-1 sm:col-span-2">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold text-slate-600 uppercase">Vehicle Image Source</label>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => setImageMode("url")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        imageMode === "url" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Use URL
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setImageMode("upload")} 
                      className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                        imageMode === "upload" 
                          ? "bg-slate-800 text-white border-slate-800 shadow-sm" 
                          : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      Upload File (S3)
                    </button>
                  </div>
                </div>

                {imageMode === "url" ? (
                  <input 
                    type="text" 
                    name="imageUrl" 
                    value={formData.imageUrl} 
                    onChange={handleInputChange} 
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-slate-50/55 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-650 outline-none transition-all" 
                    placeholder="e.g. https://images.unsplash.com/photo-..." 
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex-1 border-2 border-dashed border-slate-200 hover:border-indigo-500 hover:bg-slate-50/50 rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all">
                      <span className="text-xs text-indigo-600 font-bold">Choose Image File &rarr; Upload to S3</span>
                      <span className="text-[10px] text-slate-400 mt-1">PNG, JPG, WEBP (Max 10MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleMediaUpload(e, "image")} 
                        className="hidden" 
                      />
                    </label>
                    {formData.imageUrl && (
                      <div className="w-16 h-16 rounded-xl border border-slate-200 overflow-hidden relative flex-shrink-0 shadow-sm">
                        <img src={formData.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}
                          className="absolute inset-0 bg-red-600/90 text-white font-bold text-[10px] uppercase flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity border-0 cursor-pointer"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {formData.imageUrl && imageMode === "upload" && (
                  <p className="text-[10px] text-emerald-600 font-bold mt-1 truncate">
                    ✓ Image uploaded to S3: {formData.imageUrl}
                  </p>
                )}
              </div>
 
              <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-650 hover:bg-slate-50 hover:text-slate-800 font-semibold text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm"
                >
                  Save Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Showroom QR Code Modal */}
      {showQrModal && selectedCarForQr && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-sm overflow-hidden relative text-center text-slate-800">
            <button 
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-full transition-colors border-0 outline-none"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-xl font-bold text-slate-800 tracking-tight mb-1">Showroom QR Code</h2>
            <p className="text-slate-400 text-xs mb-6">
              {selectedCarForQr.year} {selectedCarForQr.make} {selectedCarForQr.model}
            </p>
            
            <div className="bg-slate-50 border border-slate-100 p-6 rounded-2xl inline-block shadow-inner mb-6">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/kiosk?carId=${selectedCarForQr._id}`)}`}
                alt="Showroom QR Code"
                className="w-48 h-48 mx-auto"
              />
            </div>
            
            <div className="text-slate-500 text-[11px] leading-relaxed mb-4">
              Print and place this QR code on the car window. Customers can scan it to launch the interactive kiosk guided deal flow on their mobile.
            </div>
            
            <button
              onClick={() => window.print()}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-2.5 rounded-xl text-sm transition-all"
            >
              Print QR Code
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2Cars;
